import { Link } from "wouter";
import { useI18n } from "@/lib/i18n";

/**
 * Terms of Service. Required by both Apple App Store §3.1.2 and Google
 * Play developer agreement for any app that handles user accounts. Must
 * be reachable without sign-in so OAuth review and store reviewers can
 * crawl it. Mirrors `Privacy.tsx` for layout + bilingual EN/AR.
 *
 * The body is intentionally short. Receipt is single-tenant per
 * deployment; the actual contractual text is between the customer and
 * the operator of their Receipt instance, and is delivered out-of-band.
 * What this page provides is the public-facing acceptable-use baseline.
 */
export default function Terms() {
  const { locale } = useI18n();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="container max-w-3xl py-12">
        <Link
          href="/"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          {locale === "ar" ? "→" : "←"}{" "}
          {locale === "ar" ? "العودة إلى التطبيق" : "Back to the app"}
        </Link>

        <h1 className="mt-6 text-4xl font-serif font-semibold tracking-tight">
          {locale === "ar" ? "شروط الخدمة" : "Terms of Service"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {locale === "ar" ? "آخر تحديث: 7 سبتمبر 2026" : "Last updated: September 7, 2026"}
        </p>

        <div className="mt-10 space-y-8 text-[15px] leading-7">
          {locale === "ar" ? <ArabicTerms /> : <EnglishTerms />}
        </div>
      </div>
    </div>
  );
}

function EnglishTerms() {
  return (
    <>
      <Section title="Agreement">
        <p>
          By creating an account or using Receipt (&ldquo;the Service&rdquo;)
          you agree to these Terms of Service. If you do not agree, do not use
          the Service. The Service is provided by the operator of your
          Receipt deployment (&ldquo;the Operator&rdquo;); contractual
          specifics &mdash; pricing, support SLA, data residency &mdash; are
          governed by your separate agreement with the Operator.
        </p>
      </Section>

      <Section title="Eligibility">
        <p>
          You must be at least 13 years old (or the age of digital consent in
          your jurisdiction, whichever is higher) to use Receipt. The Service
          is designed for workplace use; you affirm you have authority to
          connect any third-party integration (GitHub, Google, Slack,
          Microsoft, Exchange) on behalf of the account whose data you
          import.
        </p>
      </Section>

      <Section title="Acceptable use">
        <p>You agree not to:</p>
        <ul className="list-disc ms-6 space-y-2 mt-3">
          <li>
            Reverse-engineer, decompile, or attempt to extract the source of
            the Service except to the extent that applicable law expressly
            permits.
          </li>
          <li>
            Use Receipt to harass, defame, or surveil other users; record voice
            memos containing third-party speech without consent; or generate
            content that infringes intellectual-property rights.
          </li>
          <li>
            Submit malicious payloads, attempt to bypass rate limits, scrape at
            volumes that degrade Service availability, or impersonate other
            users.
          </li>
          <li>
            Use connected integrations in violation of those third parties&rsquo;
            terms (e.g. exporting data your account does not have rights to).
          </li>
        </ul>
      </Section>

      <Section title="Your content">
        <p>
          You retain ownership of every signal, receipt, voice memo, and edit
          you create. You grant the Operator a limited, revocable licence to
          process your content for the sole purpose of operating the Service
          on your behalf &mdash; storing it, drafting receipts from it,
          surfacing it to teammates you have explicitly added, and rendering
          the share card if you request one. Detailed handling lives in the{" "}
          <Link href="/privacy" className="underline">
            Privacy Policy
          </Link>
          .
        </p>
      </Section>

      <Section title="AI-generated drafts">
        <p>
          Receipt drafts daily and weekly summaries, and can write a private
          Your Week reflection when you ask for one, by sending the selected
          content to an external AI provider. AI output may contain
          inaccuracies; review every draft before approving or sharing it, and
          treat a reflection as a prompt for your own thinking rather than a
          record. The Operator does not guarantee that AI output is factually
          correct, complete, or suitable for any particular purpose &mdash; you
          are responsible for the accuracy of any receipt you approve. See the
          Privacy page for how AI processing works.
        </p>
      </Section>

      <Section title="Account suspension and termination">
        <p>
          The Operator may suspend or terminate your account if you breach
          these Terms, if your use creates a security or stability risk to
          the Service, or if required by law. You may terminate your account
          at any time from Settings &rarr; Delete account; the deletion is
          immediate and cannot be reversed.
        </p>
      </Section>

      <Section title="Service availability">
        <p>
          Receipt is provided on an &ldquo;as available&rdquo; basis. The
          Operator does not guarantee uninterrupted availability and is not
          liable for downtime caused by upstream third parties (your OAuth
          provider, GitHub, Google, Slack, Microsoft, etc.) or force majeure.
        </p>
      </Section>

      <Section title="Disclaimers and liability">
        <p>
          To the fullest extent permitted by law, the Service is provided
          without warranties of any kind, express or implied, including
          merchantability, fitness for a particular purpose, and
          non-infringement. The Operator&rsquo;s aggregate liability for any
          claim arising out of or related to the Service shall not exceed the
          amount you paid to the Operator in the twelve months preceding the
          claim.
        </p>
      </Section>

      <Section title="Changes to these Terms">
        <p>
          The Operator may revise these Terms from time to time. Material
          changes will be announced inside the app or via email at least 14
          days before they take effect. Continued use of the Service after a
          revision means you accept the revised Terms.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          For questions about these Terms or to report a violation, contact
          the Operator of your Receipt workspace.
        </p>
      </Section>
    </>
  );
}

function ArabicTerms() {
  return (
    <>
      <Section title="الاتفاقية">
        <p>
          باستخدامك Receipt («الخدمة») فإنك توافق على هذه الشروط. تُقدَّم
          الخدمة من قِبَل مُشغِّل نسختك من Receipt، وتفاصيل العقد
          (التسعير ودعم الخدمة وموقع البيانات) يحكمها اتفاقك المنفصل
          مع المُشغِّل.
        </p>
      </Section>

      <Section title="الأهلية">
        <p>
          يلزم أن لا يقل عمرك عن 13 عاماً (أو سن الموافقة الرقمية في
          نطاقك القضائي، أيّهما أعلى). الخدمة موجَّهة للاستخدام
          المؤسسي؛ وتقرّ بأن لديك صلاحية ربط أي تكامل خارجي بالحساب
          الذي تستورد منه البيانات.
        </p>
      </Section>

      <Section title="الاستخدام المقبول">
        <p>توافق على ألا:</p>
        <ul className="list-disc me-6 space-y-2 mt-3">
          <li>تُجري هندسة عكسية أو تستخرج كود الخدمة إلا حيث يسمح القانون.</li>
          <li>
            تستخدم Receipt للتحرّش أو التشهير أو المراقبة، أو تُسجّل
            ملاحظات صوتية تحوي كلام طرف ثالث دون موافقته.
          </li>
          <li>تُقدّم بيانات ضارة أو تتجاوز حدود المعدّل أو تنتحل شخصية مستخدم آخر.</li>
          <li>تستخدم التكاملات بما يخالف شروط تلك الأطراف الثالثة.</li>
        </ul>
      </Section>

      <Section title="محتواك">
        <p>
          تحتفظ بملكية كل إشارة وإيصال وملاحظة صوتية تنشئها. تمنح
          المُشغِّل ترخيصاً محدوداً وقابلاً للإلغاء لمعالجة محتواك بهدف
          تشغيل الخدمة نيابةً عنك. التفاصيل في{" "}
          <Link href="/privacy" className="underline">
            سياسة الخصوصية
          </Link>
          .
        </p>
      </Section>

      <Section title="مسوّدات الذكاء الاصطناعي">
        <p>
          تُصاغ المسوّدات، ويُكتب تأمّل &laquo;أسبوعك&raquo; عند طلبك، بإرسال
          المحتوى المختار إلى مزوّد ذكاء اصطناعي خارجي. قد تحوي مخرجات الذكاء
          الاصطناعي أخطاءً؛ راجع كل مسودة قبل الاعتماد أو المشاركة، وتعامل مع
          التأمّل بوصفه مادة لتفكيرك لا سجلاً. أنت المسؤول عن دقة أيّ إيصال
          تعتمده. راجع صفحة الخصوصية لمعرفة كيفية معالجة الذكاء الاصطناعي.
        </p>
      </Section>

      <Section title="إنهاء الحساب">
        <p>
          يجوز للمُشغِّل تعليق أو إنهاء حسابك عند مخالفة هذه الشروط.
          يمكنك إنهاء حسابك في أيّ وقت من «الإعدادات → حذف الحساب»
          والإجراء فوري ولا يمكن التراجع عنه.
        </p>
      </Section>

      <Section title="توافر الخدمة">
        <p>
          يُقدَّم Receipt على أساس &laquo;حسب التوافر&raquo;. لا يضمن المُشغّل
          استمرارية الخدمة دون انقطاع، ولا يتحمّل مسؤولية التوقّف الناتج عن
          أطراف ثالثة (مزوّد الدخول لديك أو GitHub أو Google أو Slack أو
          Microsoft وغيرها) أو عن قوة قاهرة.
        </p>
      </Section>

      <Section title="إخلاء المسؤولية">
        <p>
          تُقدَّم الخدمة «كما هي» دون ضمانات صريحة أو ضمنية. لا تتعدى
          مسؤولية المُشغِّل التراكمية المبلغ الذي دفعته في الأشهر الـ12
          السابقة للمطالبة.
        </p>
      </Section>

      <Section title="تعديل هذه الشروط">
        <p>
          يجوز للمُشغّل تعديل هذه الشروط من حين لآخر. ويُعلَن عن التغييرات
          الجوهرية داخل التطبيق أو عبر البريد الإلكتروني قبل 14 يوماً على
          الأقل من سريانها. واستمرارك في استخدام الخدمة بعد التعديل يعني
          قبولك الشروط المعدّلة.
        </p>
      </Section>

      <Section title="جهات الاتصال">
        <p>للأسئلة المتعلقة بهذه الشروط يرجى التواصل مع مُشغِّل مساحة عملك.</p>
      </Section>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-serif font-semibold mb-3">{title}</h2>
      <div className="text-foreground/85">{children}</div>
    </section>
  );
}
